import { useEffect, useRef, useState } from "react";

/**
 * Hook to observe any HTML element's intersection with viewport.
 *
 * @template T - Type of HTML element to observe (defaults to HTMLElement)
 * @param threshold - Optional intersection threshold (0 to 1), defaults to 0.5
 * @returns Tuple of [isIntersecting, ref]
 *
 * @example
 * -> For a div
 * const [isIntersecting, ref] = useIntersectionObserver<HTMLDivElement>();
 * <div ref={ref} />
 *
 * -> For an image
 * const [isIntersecting, ref] = useIntersectionObserver<HTMLImageElement>();
 * <img ref={ref} />
 *
 * -> With custom threshold
 * const [isIntersecting, ref] = useIntersectionObserver<HTMLElement>(0.75);
 */

export const useIntersectionObserver = <T extends HTMLElement>(
  threshold: number = 0.5
) => {
  const [isIntersecting, setIsIntersecting] = useState<boolean>(false);
  const intersectionObserverRef = useRef<T>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => setIsIntersecting(entry.isIntersecting),
      { threshold }
    );

    const element = intersectionObserverRef.current;
    if (element) {
      observer.observe(element);
    }

    return () => observer.disconnect();
  }, [threshold]);

  return [isIntersecting, intersectionObserverRef] as const;
};
